import type { JSONContent } from '@tiptap/core';

/**
 * Marker format for inline highlight colors in markdown:
 *   {{highlight:value}}text{{/highlight}}
 *
 * Similar to {{color:value}}text{{/color}} for text colors.
 */

const HIGHLIGHT_MARKER_REGEX = /\{\{highlight:([^}]+)\}\}(.*?)\{\{\/highlight\}\}/g;

// --- Save path: highlight marks → text markers ---

/**
 * Recursively converts highlight marks to {{highlight:value}}text{{/highlight}} markers.
 * This preserves inline highlight colors through markdown serialization.
 */
export function convertHighlightColorsToMarkers(node: JSONContent): JSONContent {
  if (node.type === 'text' && node.text && node.marks) {
    const highlightMark = node.marks.find((m) => m.type === 'highlight' && m.attrs?.['color']);
    if (highlightMark) {
      const color = highlightMark.attrs!['color'] as string;
      const otherMarks = node.marks.filter((m) => m !== highlightMark);
      return {
        type: 'text',
        text: `{{highlight:${color}}}${node.text}{{/highlight}}`,
        ...(otherMarks.length > 0 && { marks: otherMarks }),
      };
    }
    return node;
  }

  if (node.content) {
    return {
      ...node,
      content: node.content.map(convertHighlightColorsToMarkers),
    };
  }

  return node;
}

// --- Load path: text markers → highlight marks ---

/**
 * Recursively processes a node to convert {{highlight:value}}text{{/highlight}} markers
 * to highlight marks on text nodes.
 */
export function processNodeForHighlightColors(node: JSONContent): JSONContent {
  if (node.content) {
    return {
      ...node,
      content: processContentForHighlightColors(node.content),
    };
  }

  return node;
}

function processContentForHighlightColors(content: JSONContent[]): JSONContent[] {
  const result: JSONContent[] = [];

  for (const node of content) {
    if (node.type === 'text' && node.text) {
      const parts = splitTextWithHighlights(node.text, node.marks);
      result.push(...parts);
    } else if (node.content) {
      result.push({
        ...node,
        content: processContentForHighlightColors(node.content),
      });
    } else {
      result.push(node);
    }
  }

  return result;
}

function splitTextWithHighlights(text: string, marks?: JSONContent['marks']): JSONContent[] {
  const result: JSONContent[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(HIGHLIGHT_MARKER_REGEX)) {
    // Add text before the match
    if (match.index > lastIndex) {
      const beforeText = text.slice(lastIndex, match.index);
      result.push({ type: 'text', text: beforeText, ...(marks && { marks }) });
    }

    const color = match[1];
    const highlightedText = match[2];

    const highlightMark = { type: 'highlight', attrs: { color } };
    const combinedMarks = marks ? [...marks, highlightMark] : [highlightMark];

    result.push({
      type: 'text',
      text: highlightedText,
      marks: combinedMarks,
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    result.push({ type: 'text', text: remainingText, ...(marks && { marks }) });
  }

  // If no highlight markers were found, return original text node
  if (result.length === 0) {
    result.push({ type: 'text', text, ...(marks && { marks }) });
  }

  return result;
}
